import { createSelector } from 'reselect'
import { getPageData, getPageParams } from 'selectors/page'
import { CURATOR } from 'lib/user/constants'
import { capitalizeString } from 'lib/helpers'
import { getUserID, isAuthenticated as getIsAuthenticated } from 'selectors/user/session'
export { getIsArtist, isSessionSet } from 'selectors/user/session'
export { getIsMobileTraffic } from 'selectors/shared/mobileTraffic'
import {
  getAlgoliaDataPaginationCurrent,
  getAlgoliaDataPaginationTotal,
  getAlgoliaDataHitsResults,
} from 'selectors/algolia2/data'
import {
  StudioImagesType,
  CollectionType,
  SocialLinksType,
  ArtistArtwork,
  AccountData,
  AboutArtist,
  HeroImage,
  Badge,
  ArtistProfileDataLayerType,
  CuratorProfileDataLayerType,
  AllArtworksDataLayerType,
  ArtistProfileMetaDataType,
  MetaDataBreadCrumbType,
  AllArtworksMetaDataType,
} from '../types'
import {
  getTotalResults,
  getPaginationPageNumber,
  getPaginationResultsPerPage,
} from 'selectors/searchProvider'
export const getPageParamUserId = createSelector(
  [getPageParams],
  (pageParams): string => pageParams.userId || ''
)
export const getPageParamSection = createSelector(
  [getPageParams],
  (pageParams): string => pageParams.section || 'profile'
)
export const getIsUsersArtworksAccount = createSelector(
  [getPageParams, getUserID, getIsAuthenticated],
  (params, userId, isAuthenticated): boolean =>
    (isAuthenticated && Number(userId) === Number(params.userId || 0)) || false
)
export const getAccountData = createSelector(
  [getPageData],
  (pageData): AccountData => pageData.accountData || {}
)
export const getAboutArtist = createSelector(
  [getAccountData],
  (accountData): AboutArtist => accountData.aboutArtist || {}
)
export const getTotalFollowers = createSelector(
  [getAccountData],
  (accountData): number => accountData.followersTotal || 0
)
export const getAccountType = createSelector(
  [getPageData],
  (pageData): string => pageData.accountType || 'Artist'
)
export const getFullName = createSelector(
  [getAccountData],
  ({ firstName, lastName }): string => `${firstName} ${lastName}`
)
export const getCountry = createSelector(
  [getAccountData],
  (accountData): string => accountData.country || ''
)
export const getUserName = createSelector(
  [getAccountData],
  (accountData): string => accountData.userName || ''
)
export const getAvatar = createSelector(
  [getAccountData],
  (accountData): string => accountData.avatar || ''
)
export const getAvatarLarge = createSelector(
  [getAccountData],
  (accountData): string => accountData.avatarLarge || ''
)
export const getCanIndex = createSelector(
  [getAccountData],
  (accountData): boolean => accountData.canIndex || false
)
export const getAccountUserID = createSelector(
  [getAccountData],
  (accountData): number => accountData.userId || 0
)
export const getEmail = createSelector(
  [getAccountData],
  (accountData): string => accountData.email || ''
)
export const getSocialLinks = createSelector(
  [getAccountData],
  (accountData): SocialLinksType => accountData.socialLinks || {}
)
export const getHeroImages = createSelector(
  [getAccountData],
  (accountData): HeroImage => accountData.hero || {}
)
export const getArtworks = createSelector(
  [getAccountData],
  (accountData): ReadonlyArray<ArtistArtwork> => accountData.artworks || []
)
export const getReasonsAllowedToSell = createSelector(
  [getAccountData],
  (accountData): ReadonlyArray<string> => accountData.reasonsNotAllowedToSell || []
)
export const getArtworksLength = createSelector(
  [getAccountData],
  (accountData): number => accountData.artworks?.length || 0
)
export const getCollections = createSelector(
  [getAccountData],
  (accountData): ReadonlyArray<CollectionType> => accountData.collections || []
)
export const getInsideTheStudioImages = createSelector(
  [getAccountData],
  (accountData): StudioImagesType => accountData.insideTheStudioImages || []
)
export const getArtworksTotal = createSelector(
  [getAccountData],
  (accountData): number => accountData.artworksTotal || 0
)
export const getBadgeTitles = createSelector(
  [getAccountData],
  (accountData): ReadonlyArray<string> =>
    accountData.badges ? accountData.badges.map((item) => item.title) : '' || []
)
export const getBadges = createSelector(
  [getAccountData],
  (accountData): ReadonlyArray<Badge> => accountData.badges || []
)
export const getArtistDataLayer = createSelector(
  [getAccountUserID, getFullName, getCountry, getBadgeTitles],
  (userId, fullName, country, badgeTitles): ArtistProfileDataLayerType => ({
    environment: 'production',
    event: 'pagetype',
    pagetype: 'userProfile',
    subpagetype: 'profile',
    'artist country': country,
    'artist id': userId,
    'artist name': fullName,
    badges: badgeTitles,
  })
)
export const getCuratorDataLayer = createSelector(
  [getAccountUserID, getFullName, getCountry],
  (userId, fullName, country): CuratorProfileDataLayerType => ({
    environment: 'production',
    event: 'pagetype',
    pagetype: 'curatorProfile',
    subpagetype: 'profile',
    'curator country': country,
    'user id': userId,
    'curator name': fullName,
  })
)
export const getAllArtworksDataLayer = createSelector(
  getPageData,
  (data): AllArtworksDataLayerType => {
    const layerData = {
      pagetype: 'artistProfile',
      subpagetype: 'artworks',
      subsubpagetype: '',
    }
    layerData.subsubpagetype = `${data.firstName} ${data.lastName}` || ''
    return layerData
  }
)
export const getCanRequestCommission = createSelector(
  [getCanIndex, getUserID, getAccountUserID],
  (canIndex, sessionUserId, accountUserId): boolean => {
    const myOwnAccount = sessionUserId === accountUserId
    return !myOwnAccount && canIndex
  }
)
export const getProfileMetaData = createSelector(
  [getAccountType, getAvatar, getFullName, getArtworksTotal],
  (accountType, avatar, fullName, artworksTotal): ArtistProfileMetaDataType => {
    const isAccountTypeCurator = accountType === CURATOR
    const descriptionText = isAccountTypeCurator ? 'curated by' : 'like'
    const title = `${capitalizeString(fullName)} | Saatchi Art`
    const description = `View ${fullName}’s Profile on Saatchi Art. Find art for sale at great prices from artists including Paintings, Photography, Sculpture, and Prints by Top Emerging Artists ${descriptionText} ${fullName}.`
    return {
      description,
      title,
      robots: Boolean(artworksTotal) || isAccountTypeCurator,
      og: {
        description,
        image: avatar,
        title,
        type: 'article',
      },
      preloadImages: [],
      schema: {
        webpage: {
          headline: title,
          description,
        },
      },
    }
  }
)

export const getBreadcrumbSchema = createSelector(
  [getAccountUserID, getFullName],
  (artistUserId, artistName): MetaDataBreadCrumbType => {
    if (!process.env.SITE_URL) {
      throw new Error('Please define SITE_URL in .env')
    }

    const siteUrl = process.env.SITE_URL
    const capitalizedArtistName = capitalizeString(artistName)

    return {
      '@context': 'http://schema.org',
      type: 'BreadcrumbList',
      itemListElement: [
        {
          type: 'ListItem',
          position: 1,
          item: {
            '@id': `${siteUrl}/account/profile/${artistUserId}`,
            name: `${capitalizedArtistName} Artist Profile`,
          },
        },
        {
          type: 'ListItem',
          position: 2,
          item: {
            '@id': `${siteUrl}/account/artworks/${artistUserId}`,
            name: `${capitalizedArtistName} Artworks`,
          },
        },
      ],
    }
  }
)

export const getHitsCount = createSelector(
  [getAlgoliaDataHitsResults],
  (results): number => results.length
)
export const getHitsAvailableForSaleCount = createSelector(
  [getAlgoliaDataHitsResults],
  (results): number =>
    results.reduce((count, { originalStatus }) => (count += originalStatus === 'avail' ? 1 : 0), 0)
)

// {"@context":"http://schema.org","@type":"BreadcrumbList","itemListElement":[{"type":"ListItem","position":1,"item":{"@id":"https://www.qa.saatchiart.com","name":"Home"}},{"type":"ListItem","position":2,"item":{"@id":"https://www.qa.saatchiart.com/account-artworks","name":"Account/Artworks"}}]}
export const getAllArtworksMetaData = createSelector(
  [
    getFullName,
    getBreadcrumbSchema,
    getTotalResults,
    getAvatar,
    getPaginationPageNumber,
    getPaginationResultsPerPage,
  ],
  (
    artistName,
    breadcrumb,
    hitsCount,
    avatarURL,
    currentPage,
    resultsPerPage
  ): AllArtworksMetaDataType => {
    const capitalizedArtistName = capitalizeString(artistName)
    const description = `View ${capitalizedArtistName}'s artworks on Saatchi Art. Find original art for sale at great prices, including Paintings, Sculptures, Photography, Drawings, and Art Prints from emerging artists like ${capitalizedArtistName}`
    const title = `${capitalizedArtistName} Artworks | Saatchi Art`
    return {
      description,
      title,
      og: {
        description,
        image: avatarURL,
        title,
        type: 'article',
      },
      page: {
        current: currentPage,
        prev: currentPage === 2 || currentPage > 2,
        next: currentPage !== Math.ceil(hitsCount / resultsPerPage),
      },
      preloadImages: [],
      robots: hitsCount > 10,
      // if PRODUCTS for Sale
      schema: {
        breadcrumb,
        webpage: {
          description,
          headline: title,
        },
      },
    }
  }
)
export const getFeaturedCollections = createSelector(
  [getAccountData],
  (accountData): ReadonlyArray<CollectionType> => accountData.featuredCollections || []
)
